import {
  Check as CheckIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  FolderOutlined as FolderIcon,
  History as HistoryIcon,
  MessageOutlined as MessageIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  Send as SendIcon,
  AccessTime as TimeIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CardActions,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import AppDialog from '../components/AppDialog';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { investigationChatStream } from '../services/api';
import { updateInvestigationMessage } from '../services/firestore';
import { IMG } from '../theme/assets';
import { verdictKeyFromLabel, verdictSealFromLabel } from '../theme/designTokens';
import type { InvestigationItem, InvestigationMessage, SearchPeriodConfig } from '../types';

/** 調査エージェント種別 → 表示ラベル / アイコン画像 */
const AGENT_META: Record<string, { label: string; icon: string }> = {
  general_conversation: { label: 'MIKIWAMEアシスタント', icon: IMG.chat.avatar },
  strategy_planning: { label: '戦略立案エージェント', icon: IMG.investigate.strategy },
  perplexity_search: { label: '横断検索エージェント', icon: IMG.investigate.webSearch },
  gemini_grounding: { label: 'Web検索（根拠付き）', icon: IMG.investigate.webSearch },
};

const InvestigationPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // フリーチャットアイテムかどうかを判定するヘルパー関数
  const isFreeChatItem = (item: InvestigationItem | null): boolean => {
    if (!item) return false;
    return item.project_id === '' && item.project_title === 'フリーチャット調査';
  };

  // 相対時刻表示（たった今 / 3時間前 / 昨日 18:55 / 06/14）
  const formatRelative = (iso: string): string => {
    const d = new Date(iso);
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return 'たった今';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}分前`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}時間前`;
    if (diffSec < 172800)
      return `昨日 ${d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}`;
    return d.toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' });
  };

  // State - 統合構造対応
  const [items, setItems] = useState<InvestigationItem[]>([]); // 新しい統合アイテム
  const [currentItem, setCurrentItem] = useState<InvestigationItem | null>(null); // 現在の調査アイテム

  const [messages, setMessages] = useState<InvestigationMessage[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false); // ストリーミング中フラグ
  const [streamingContent, setStreamingContent] = useState(''); // ストリーミング中のテキスト
  const [streamingAgent, setStreamingAgent] = useState<string>(''); // ストリーミング中のエージェント名
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<
    'strategy_planning' | 'perplexity_search' | null
  >(null); // エージェント予約
  const [searchConfig, setSearchConfig] = useState<SearchPeriodConfig | null>(null); // 期間指定設定

  // UI管理用State
  const [activeTab, setActiveTab] = useState(0); // 0: 調査対象, 1: 会話履歴

  // Chat scroll reference
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 新しい統合構造用の初期化関数
  const initializeUnifiedInvestigation = useCallback(async () => {
    if (!user) return;

    try {
      console.log('🆕 統合Investigation初期化開始');

      if (urlSessionId) {
        // 既存の調査アイテム復元 - フロントエンドFirestoreから直接取得
        const itemRef = doc(db, 'investigation_items', urlSessionId);
        const itemDoc = await getDoc(itemRef);
        if (itemDoc.exists()) {
          const item = { id: itemDoc.id, ...itemDoc.data() } as InvestigationItem;
          setCurrentItem(item);

          if (item.status === 'investigating') {
            setMessages(item.messages || []);
            setActiveTab(1); // 会話履歴タブ
            console.log('🔄 既存調査復元:', item.id, `(status: ${item.status})`);
          } else {
            // waiting状態の場合はメッセージをクリア
            setMessages([]);
            setActiveTab(0); // 調査対象タブ
          }
        }
      }

      // 調査アイテム一覧をフロントエンドFirestoreから取得
      const itemsQuery = query(
        collection(db, 'investigation_items'),
        where('userId', '==', user.uid),
      );
      const querySnapshot = await getDocs(itemsQuery);
      const items: InvestigationItem[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        items.push({
          id: data.id,
          userId: data.userId,
          project_id: data.project_id,
          project_title: data.project_title,
          fact_index: data.fact_index,
          claim: data.claim,
          verification_result: data.verification_result,
          verification_summary: data.verification_summary || '',
          confidence: data.confidence,
          added_at: data.added_at,
          status: data.status,
          updated_at: data.updated_at,
          investigation_started_at: data.investigation_started_at,
          title: data.title,
          messages: data.messages || [],
          message_count: data.message_count || 0,
        });
      });

      items.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      setItems(items);
      console.log('📋 調査アイテム一覧取得完了:', items.length, '件');

      console.log('✅ 統合Investigation初期化完了');
    } catch (error) {
      console.error('❌ 統合Investigation初期化エラー:', error);
    }
  }, [user?.uid, user, urlSessionId]);

  useEffect(() => {
    if (user) {
      // 統合Investigation初期化
      initializeUnifiedInvestigation();
    }
  }, [
    user, // 統合Investigation初期化
    initializeUnifiedInvestigation,
  ]);

  useEffect(() => {
    // メッセージが追加されたら最下部にスクロール
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Firestoreへの保存処理を共通化
  const saveConversationToFirestore = async (
    activeItem: InvestigationItem,
    userMessage: InvestigationMessage,
    assistantMessage: InvestigationMessage,
  ) => {
    const currentMessages = [...(activeItem.messages || []), userMessage, assistantMessage];

    // Firestore保存用にundefined値を除去
    const cleanMessages = currentMessages.map((msg) => {
      const cleanMsg = { ...msg };
      if (cleanMsg.fact_reference === undefined) {
        delete cleanMsg.fact_reference;
      }
      return cleanMsg;
    });

    const updatedItem: InvestigationItem = {
      ...activeItem,
      status: 'investigating' as const,
      title:
        activeItem.title ||
        userMessage.content.slice(0, 50) + (userMessage.content.length > 50 ? '...' : ''),
      messages: cleanMessages,
      message_count: cleanMessages.length,
      investigation_started_at: activeItem.investigation_started_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      const itemRef = doc(db, 'investigation_items', activeItem.id);
      const docSnap = await getDoc(itemRef);

      if (!docSnap.exists()) {
        await setDoc(itemRef, updatedItem);
        console.log('✅ 新規調査アイテムをFirestoreに作成完了');
      } else {
        await updateDoc(itemRef, {
          status: updatedItem.status,
          title: updatedItem.title,
          messages: updatedItem.messages,
          message_count: updatedItem.message_count,
          investigation_started_at: updatedItem.investigation_started_at,
          updated_at: updatedItem.updated_at,
        });
        console.log('✅ Firestoreに会話履歴保存完了');
      }

      setCurrentItem(updatedItem);
      setItems((prevItems) => {
        const index = prevItems.findIndex((item) => item.id === activeItem.id);
        if (index === -1) return [updatedItem, ...prevItems];
        const newItems = [...prevItems];
        newItems[index] = updatedItem;
        return newItems;
      });
    } catch (error) {
      console.error('❌ Firestore保存エラー:', error);
    }
  };

  // 統合版メッセージ送信処理
  const handleSendMessageUnified = async (message: string) => {
    if (!user) return;

    setIsLoading(true);
    setCurrentInput('');
    setStreamingContent('');
    setStreamingAgent('');

    try {
      let activeItem = currentItem;

      // フリーチャットモードの場合、新規InvestigationItemを作成
      if (!activeItem) {
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const newItemId = `free-chat-${timestamp}-${randomId}`;

        activeItem = {
          id: newItemId,
          userId: user.uid,
          project_id: '',
          project_title: 'フリーチャット調査',
          fact_index: -1,
          claim: message.slice(0, 100),
          verification_result: '',
          verification_summary: '',
          confidence: 0,
          added_at: new Date().toISOString(),
          status: 'investigating',
          investigation_started_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          title: message.slice(0, 50) + (message.length > 50 ? '...' : ''),
          messages: [],
          message_count: 0,
        };
      }

      // ユーザーメッセージを作成
      const userMessage: InvestigationMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        fact_reference: activeItem.project_id
          ? {
              project_id: activeItem.project_id,
              project_title: activeItem.project_title,
              fact_index: activeItem.fact_index,
              claim: activeItem.claim,
            }
          : undefined,
      };

      setMessages((prev) => [...prev, userMessage]);

      // APIパラメータを構築
      const apiParams: any = {
        session_id: activeItem.id,
        user_id: user.uid,
        message: message,
        agent_type: selectedAgent || 'auto',
        mode: currentItem ? 'fact_based' : 'free_chat',
        selected_target: activeItem,
        conversation_history: [...(activeItem.messages || []), userMessage],
      };

      if (selectedAgent === 'perplexity_search' && searchConfig) {
        apiParams.search_config = searchConfig;
      }

      // ストリーミング開始
      setIsStreaming(true);

      let finalAssistantMessage: InvestigationMessage | null = null;

      await investigationChatStream(apiParams, async (event, data) => {
        if (event === 'start' && data) {
          setStreamingAgent(data.agent_type);
          setIsLoading(false);
        } else if (event === 'chunk' && data) {
          setStreamingContent((prev) => prev + (data.content || ''));
        } else if (event === 'message' && data) {
          finalAssistantMessage = {
            id: data.message_id,
            role: 'assistant',
            content: data.content,
            agent: data.agent_type,
            // backend は tz 無しの naive UTC(isoformat) を返すため受信側の正確な時刻を使う
            timestamp: new Date().toISOString(),
          };
        } else if (event === 'done') {
          if (finalAssistantMessage) {
            setMessages((prev) => [...prev, finalAssistantMessage!]);
            setStreamingContent('');
            setIsStreaming(false);
            await saveConversationToFirestore(activeItem!, userMessage, finalAssistantMessage!);
          }
        } else if (event === 'error') {
          console.error('Streaming error:', data?.content);
        }
      });

      setSelectedAgent(null);
      setSearchConfig(null);
      setActiveTab(1);
    } catch (error) {
      console.error('❌ 統合版メッセージ送信エラー:', error);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleStrategyPlanning = () => {
    // エージェントを予約（トグル動作）
    setSelectedAgent((prev) => (prev === 'strategy_planning' ? null : 'strategy_planning'));
  };

  const handleCrossSearch = () => {
    // エージェントを予約（トグル動作）
    const newSelection = selectedAgent === 'perplexity_search' ? null : 'perplexity_search';
    setSelectedAgent(newSelection);

    // 横断検索が選択された場合、期間指定ダイアログを表示
    if (newSelection === 'perplexity_search') {
      setSearchDialogOpen(true);
    } else {
      setSearchConfig(null); // 選択解除時は期間設定もクリア
    }
  };

  // メッセージ編集処理
  const handleEditMessage = async (messageId: string, newContent: string) => {
    if (!user || !currentItem) return;

    try {
      // Firestoreでメッセージを更新
      await updateInvestigationMessage(currentItem.id, messageId, newContent);

      // ローカルのメッセージも更新
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: newContent, edited: true, lastEdited: new Date().toISOString() }
            : msg,
        ),
      );
    } catch (error) {
      console.error('メッセージの編集に失敗しました:', error);
    }
  };

  // 戦略実行処理
  const handleExecuteStrategy = async (strategyContent: string) => {
    if (!user || !currentItem) return;

    // 自動生成メッセージを作成（戦略内容を含む）
    const autoMessage = '上記戦略に沿って、調査してください。';

    // メッセージを画面に追加
    const userMessage: InvestigationMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: autoMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // gemini_groundingを強制実行
    setIsLoading(true);
    setStreamingContent('');
    setStreamingAgent('');
    setIsStreaming(true);

    try {
      const messageWithStrategy = `${autoMessage}\n\n参考戦略:\n${strategyContent}`;
      const apiParams: any = {
        session_id: currentItem.id,
        user_id: user.uid,
        message: messageWithStrategy,
        agent_type: 'gemini_grounding', // エージェントを強制指定
        mode: currentItem.project_id ? 'fact_based' : 'free_chat',
        selected_target: currentItem,
        conversation_history: [...messages, userMessage],
      };

      let finalAssistantMessage: InvestigationMessage | null = null;

      await investigationChatStream(apiParams, async (event, data) => {
        if (event === 'start' && data) {
          setStreamingAgent(data.agent_type);
          setIsLoading(false);
        } else if (event === 'chunk' && data) {
          setStreamingContent((prev) => prev + (data.content || ''));
        } else if (event === 'message' && data) {
          finalAssistantMessage = {
            id: data.message_id,
            role: 'assistant',
            content: data.content,
            agent: data.agent_type,
            // backend は tz 無しの naive UTC(isoformat) を返すため受信側の正確な時刻を使う
            timestamp: new Date().toISOString(),
          };
        } else if (event === 'done') {
          if (finalAssistantMessage) {
            setMessages((prev) => [...prev, finalAssistantMessage!]);
            setStreamingContent('');
            setIsStreaming(false);
            await saveConversationToFirestore(currentItem, userMessage, finalAssistantMessage!);
          }
        } else if (event === 'error') {
          console.error('Execute strategy streaming error:', data?.content);
        }
      });
    } catch (error) {
      console.error('戦略実行に失敗しました:', error);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleSearchWithPeriod = (periodConfig: SearchPeriodConfig) => {
    // 期間設定を保存してダイアログを閉じる
    setSearchConfig(periodConfig);
    setSearchDialogOpen(false);
  };

  // 一覧の分割（タブ別・件数表示用）
  const waitingItems = items.filter((item) => item.status === 'waiting');
  const investigatingItems = items
    .filter((item) => item.status === 'investigating')
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  // 横断検索ボタンに表示する期間ラベル
  const searchPeriodLabel = searchConfig
    ? searchConfig.period_type === 'day'
      ? '1日'
      : searchConfig.period_type === 'week'
        ? '1週間'
        : searchConfig.period_type === 'month'
          ? '1ヶ月'
          : searchConfig.period_type === 'year'
            ? '1年'
            : ''
    : '';

  return (
    <Box
      sx={{
        height: 'calc(var(--app-vh) - 64px)', // AppBarの高さ分のみを引く
        mt: -4, // LayoutのpaddingTop (32px) を相殺
        mb: -4, // LayoutのpaddingBottom (32px) を相殺
        py: 3,
        px: { xs: 1, md: 2 },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ヘッダー。左メニューから移動できるので戻るボタンは置かない */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>
            調査チャット
          </Typography>
        </Box>

        {/* メインレイアウト：左右分割 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '370px 1fr' },
            gap: 2.5,
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* 左パネル：コンテキスト + タブ式調査管理 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minHeight: 0 }}>
            {/* コンテキストカード（モード別） */}
            {currentItem && !isFreeChatItem(currentItem) ? (
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: '13px',
                  p: '16px 18px',
                  boxShadow: '0 8px 24px rgba(30,30,70,.06)',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 1.25 }}>
                  <Box
                    component="img"
                    src={IMG.investigate.target}
                    alt=""
                    sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: tokens.muted }}>
                    調査対象のファクト
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.5, color: tokens.ink }}
                >
                  {currentItem.claim}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: '11px' }}>
                  {(() => {
                    const v = tokens.verdict[verdictKeyFromLabel(currentItem.verification_result)];
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          fontSize: 11.5,
                          fontWeight: 900,
                          px: 1.4,
                          py: '3px',
                          borderRadius: 999,
                          bgcolor: v.tint,
                          color: v.color,
                        }}
                      >
                        <Box
                          component="img"
                          src={verdictSealFromLabel(currentItem.verification_result)}
                          alt=""
                          sx={{ width: 16, height: 16, objectFit: 'contain' }}
                        />
                        {currentItem.verification_result}
                      </Box>
                    );
                  })()}
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: tokens.muted,
                    lineHeight: 1.55,
                    borderTop: `1px solid ${tokens.line}`,
                    pt: 1.25,
                  }}
                >
                  {currentItem.verification_summary || currentItem.verification_result}
                </Typography>
              </Box>
            ) : (currentItem && isFreeChatItem(currentItem)) || !currentItem ? (
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: '13px',
                  p: '16px 18px',
                  boxShadow: '0 8px 24px rgba(30,30,70,.06)',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 1 }}>
                  <Box
                    component="img"
                    src={IMG.chat.avatar}
                    alt=""
                    sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 900, color: tokens.muted }}>
                    フリーチャットモード
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: tokens.muted, lineHeight: 1.55 }}>
                  自由なトピックでマルチエージェントと対話しながら調査します
                </Typography>
              </Box>
            ) : null}

            {/* タブ（セグメントコントロール：トラック上にアクティブが白カードで浮く） */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                flexShrink: 0,
                p: '4px',
                borderRadius: '13px',
                bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(13,148,136,0.06)',
                border: `1px solid ${tokens.line}`,
              }}
            >
              {[
                {
                  i: 0,
                  label: '調査対象',
                  count: waitingItems.length,
                  icon: <SearchIcon sx={{ fontSize: 15 }} />,
                },
                {
                  i: 1,
                  label: '会話履歴',
                  count: investigatingItems.length,
                  icon: <HistoryIcon sx={{ fontSize: 15 }} />,
                },
              ].map((t) => {
                const on = activeTab === t.i;
                return (
                  <Box
                    key={t.i}
                    component="button"
                    onClick={() => setActiveTab(t.i)}
                    sx={{
                      flex: 1,
                      fontFamily: 'inherit',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.7,
                      fontSize: 12.5,
                      fontWeight: 800,
                      py: 0.95,
                      borderRadius: '10px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: '0.18s ease',
                      bgcolor: on
                        ? themeMode === 'dark'
                          ? 'rgba(45,212,191,0.16)'
                          : '#fff'
                        : 'transparent',
                      color: on ? tokens.teal : tokens.muted,
                      boxShadow: on ? '0 2px 8px rgba(13,148,136,0.14)' : 'none',
                      '&:hover': { color: on ? tokens.teal : tokens.ink },
                    }}
                  >
                    {t.icon}
                    {t.label}
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 900,
                        px: 0.75,
                        borderRadius: 999,
                        color: on ? tokens.teal : tokens.muted,
                        bgcolor: on
                          ? themeMode === 'dark'
                            ? 'rgba(45,212,191,0.22)'
                            : 'rgba(13,148,136,0.12)'
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.06)',
                      }}
                    >
                      {t.count}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* リスト（スクロール） */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.1,
                pr: 0.5,
              }}
            >
              {activeTab === 0 && (
                <>
                  {/* 選択解除ボタン（currentItemベース） */}
                  {currentItem && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setCurrentItem(null);
                        setMessages([]);
                        navigate('/investigation');
                      }}
                      startIcon={<ClearIcon sx={{ fontSize: '16px' }} />}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.6,
                        borderRadius: '11px',
                        color: tokens.muted,
                        borderColor: tokens.line,
                        '&:hover': { borderColor: tokens.teal, color: tokens.teal },
                      }}
                    >
                      選択解除（フリーチャットモード）
                    </Button>
                  )}

                  {/* 統合調査アイテム一覧（waiting状態のみ表示） */}
                  {waitingItems.map((item) => {
                    const on = currentItem?.id === item.id;
                    const dark = themeMode === 'dark';
                    const v = tokens.verdict[verdictKeyFromLabel(item.verification_result)];
                    // 会話履歴と統一：クール系（白〜淡ブルーグレー）＋左アクセントバー（判定色）。
                    const craftBg = dark
                      ? 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))'
                      : 'linear-gradient(180deg, #ffffff, #f4f7fc)';
                    const craftBorder = dark ? tokens.line : '#e5eaf1';
                    return (
                      <Box
                        key={item.id}
                        onClick={() => {
                          setCurrentItem(item);
                          setMessages([]); // 前の会話履歴をクリア
                          navigate(`/investigation/${item.id}`);
                        }}
                        sx={{
                          position: 'relative',
                          flexShrink: 0,
                          overflow: 'hidden',
                          background: on
                            ? `linear-gradient(180deg, ${tokens.heroTo}, ${tokens.paper})`
                            : craftBg,
                          border: `1px solid ${on ? tokens.teal : craftBorder}`,
                          borderRadius: '13px',
                          p: '12px 14px 12px 18px',
                          cursor: 'pointer',
                          transition: '0.18s ease',
                          boxShadow: on
                            ? `0 6px 18px ${tokens.teal}22`
                            : '0 2px 8px rgba(60,50,20,.05)',
                          // 左の判定色アクセントバー
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3.5,
                            borderRadius: '0 4px 4px 0',
                            background: v.color,
                            opacity: on ? 1 : 0.55,
                            transition: '0.18s ease',
                          },
                          '&:hover': {
                            borderColor: `${tokens.teal}80`,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 10px 22px ${tokens.teal}1f`,
                            '&::before': { opacity: 1 },
                          },
                        }}
                      >
                        {/* 上段：判定シール + 主張 */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                          <Box
                            component="img"
                            src={verdictSealFromLabel(item.verification_result)}
                            alt=""
                            sx={{
                              width: 30,
                              height: 30,
                              flexShrink: 0,
                              objectFit: 'contain',
                              filter: 'drop-shadow(0 2px 4px rgba(30,30,70,.16))',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: tokens.ink,
                              lineHeight: 1.45,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              pr: 1.5,
                            }}
                          >
                            {item.claim}
                          </Typography>
                        </Box>

                        {/* 検証要約スニペット */}
                        {item.verification_summary && (
                          <Box
                            sx={{
                              pl: 1.25,
                              ml: 0.25,
                              mb: 0.85,
                              borderLeft: `2px solid ${dark ? 'rgba(45,212,191,0.4)' : '#d4dcea'}`,
                              fontSize: 11,
                              fontStyle: 'italic',
                              lineHeight: 1.45,
                              color: tokens.muted,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.verification_summary}
                          </Box>
                        )}

                        {/* フッター：プロジェクト + 判定ラベル */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            fontSize: 10.5,
                            fontWeight: 800,
                            color: tokens.faint,
                          }}
                        >
                          <Box
                            component={RouterLink}
                            to={`/projects/${item.project_id}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.4,
                              minWidth: 0,
                              textDecoration: 'none',
                              color: 'inherit',
                              '&:hover': { color: tokens.teal, textDecoration: 'underline' },
                            }}
                          >
                            <FolderIcon sx={{ fontSize: 12 }} />
                            <Box
                              component="span"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.project_title}
                            </Box>
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              ml: 'auto',
                              flexShrink: 0,
                              fontSize: 10,
                              fontWeight: 900,
                              px: 1,
                              py: '2px',
                              borderRadius: 999,
                              bgcolor: v.tint,
                              color: v.color,
                            }}
                          >
                            {item.verification_result}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

                  {waitingItems.length === 0 && (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 1.75,
                        py: 5,
                        color: tokens.muted,
                      }}
                    >
                      <Box
                        component="img"
                        src={IMG.investigate.target}
                        alt=""
                        sx={{
                          width: 96,
                          height: 96,
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 8px 16px rgba(30,30,70,.16))',
                        }}
                      />
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: tokens.ink }}>
                        調査対象がありません
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/items')}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '11px',
                          color: tokens.teal,
                          borderColor: `${tokens.teal}66`,
                          '&:hover': { borderColor: tokens.teal },
                        }}
                      >
                        プロジェクトから追加
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {activeTab === 1 && (
                <>
                  {/* 会話履歴タブ - 調査が開始されたアイテム一覧（最新順） */}
                  {investigatingItems.length > 0 ? (
                    investigatingItems.map((item) => {
                      const on = currentItem?.id === item.id;
                      const dark = themeMode === 'dark';
                      // 最後のメッセージのスニペット（Markdown記号を軽く除去）
                      const lastMsg = item.messages?.[item.messages.length - 1];
                      const preview = (lastMsg?.content || item.claim || '')
                        .replace(/[#*`>_~[\]()-]/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()
                        .slice(0, 48);
                      const dateLabel = formatRelative(item.updated_at);
                      // リサーチカード：クール系（白〜淡ブルーグレー）＋左アクセントバー
                      const craftBg = dark
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))'
                        : 'linear-gradient(180deg, #ffffff, #f4f7fc)';
                      const craftBorder = dark ? tokens.line : '#e5eaf1';
                      return (
                        <Box
                          key={`history-${item.id}`}
                          onClick={() => {
                            setCurrentItem(item);
                            setMessages(item.messages || []); // 会話履歴タブからは履歴を復元
                            navigate(`/investigation/${item.id}`);
                          }}
                          sx={{
                            position: 'relative',
                            flexShrink: 0,
                            overflow: 'hidden',
                            background: on
                              ? `linear-gradient(180deg, ${tokens.heroTo}, ${tokens.paper})`
                              : craftBg,
                            border: `1px solid ${on ? tokens.teal : craftBorder}`,
                            borderRadius: '13px',
                            p: '12px 14px 12px 18px',
                            cursor: 'pointer',
                            transition: '0.18s ease',
                            boxShadow: on
                              ? `0 6px 18px ${tokens.teal}22`
                              : '0 2px 8px rgba(60,50,20,.05)',
                            // 左のティール→インディゴ アクセントバー
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3.5,
                              borderRadius: '0 4px 4px 0',
                              background: `linear-gradient(180deg, ${tokens.teal}, ${tokens.indigo})`,
                              opacity: on ? 1 : 0.4,
                              transition: '0.18s ease',
                            },
                            '&:hover': {
                              borderColor: `${tokens.teal}80`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 10px 22px ${tokens.teal}1f`,
                              '&::before': { opacity: 1 },
                            },
                          }}
                        >
                          {/* 上段：owlスタンプ + タイトル（clipboard owl／このページ内で重複なし） */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                            <Box
                              component="img"
                              src={IMG.owl.clipboard}
                              alt=""
                              sx={{
                                width: 28,
                                height: 28,
                                flexShrink: 0,
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 2px 4px rgba(30,30,70,.16))',
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: 13,
                                color: tokens.ink,
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                pr: 1.5,
                              }}
                            >
                              {item.title || `${item.claim.substring(0, 40)}...`}
                            </Typography>
                          </Box>

                          {/* 引用プレビュー */}
                          {preview && (
                            <Box
                              sx={{
                                pl: 1.25,
                                ml: 0.25,
                                mb: 0.85,
                                borderLeft: `2px solid ${dark ? 'rgba(45,212,191,0.4)' : '#d4dcea'}`,
                                fontSize: 11,
                                fontStyle: 'italic',
                                lineHeight: 1.45,
                                color: tokens.muted,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {preview}…
                            </Box>
                          )}

                          {/* フッター：プロジェクト + 件数 + 日付 */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              fontSize: 10.5,
                              fontWeight: 800,
                              color: tokens.faint,
                            }}
                          >
                            {isFreeChatItem(item) ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.4,
                                  minWidth: 0,
                                }}
                              >
                                <MessageIcon sx={{ fontSize: 12 }} />
                                <Box
                                  component="span"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.project_title}
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                component={RouterLink}
                                to={`/projects/${item.project_id}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.4,
                                  minWidth: 0,
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  '&:hover': { color: tokens.teal, textDecoration: 'underline' },
                                }}
                              >
                                <FolderIcon sx={{ fontSize: 12 }} />
                                <Box
                                  component="span"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.project_title}
                                </Box>
                              </Box>
                            )}
                            <Box
                              component="span"
                              sx={{
                                ml: 'auto',
                                flexShrink: 0,
                                color: tokens.muted,
                                bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(13,148,136,0.08)',
                                borderRadius: 999,
                                px: 0.75,
                              }}
                            >
                              {item.message_count || 0}件
                            </Box>
                            <Box component="span" sx={{ flexShrink: 0, color: tokens.faint }}>
                              {dateLabel}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 1,
                        py: 5,
                        color: tokens.muted,
                      }}
                    >
                      <HistoryIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: tokens.ink }}>
                        まだ会話履歴がありません
                      </Typography>
                      <Typography sx={{ fontSize: 12.5 }}>
                        調査を開始すると履歴が表示されます
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* 右パネル：チャットエリア */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: tokens.paper,
              border: `1px solid ${tokens.line}`,
              borderRadius: '14px',
              boxShadow: '0 10px 30px rgba(30,30,70,.07)',
              overflow: 'hidden',
            }}
          >
            {/* チャットヘッダー */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: '14px 20px',
                borderBottom: `1px solid ${tokens.line}`,
                background:
                  themeMode === 'dark'
                    ? `linear-gradient(120deg, ${tokens.heroTo}, ${tokens.heroFrom})`
                    : 'linear-gradient(120deg, #eef6f3, #f1eefb)',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fdf6e3, #fff)',
                  border: '1px solid rgba(201,162,77,.35)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  boxShadow: '0 3px 8px rgba(30,30,70,.08)',
                }}
              >
                <Box
                  component="img"
                  src={IMG.chat.avatar}
                  alt=""
                  sx={{ width: 38, height: 38, objectFit: 'contain' }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: tokens.ink }}>
                  ミキワメ調査アシスタント
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontSize: 11,
                    color: tokens.muted,
                    mt: '2px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: '#16a34a',
                      boxShadow: '0 0 0 3px rgba(22,163,74,.18)',
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {currentItem
                      ? `調査中: ${currentItem.claim.substring(0, 30)}...`
                      : 'フリーチャットモード'}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* チャット履歴 */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                p: '22px 22px 8px',
                background:
                  themeMode === 'dark'
                    ? 'transparent'
                    : 'linear-gradient(180deg, #fbfcff, #f6faf9)',
              }}
            >
              <ChatHistory
                messages={messages}
                onEditMessage={handleEditMessage}
                onExecuteStrategy={handleExecuteStrategy}
                isLoading={isLoading}
                isStreaming={isStreaming}
                streamingAgent={streamingAgent}
                streamingContent={streamingContent}
                themeMode={themeMode}
                userPhotoURL={user?.photoURL ?? undefined}
                userDisplayName={user?.displayName ?? undefined}
              />
              <div ref={messagesEndRef} />
            </Box>

            {/* アクションエリア */}
            <Box
              sx={{
                flexShrink: 0,
                borderTop: `1px solid ${tokens.line}`,
                p: '12px 16px 14px',
                background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
              }}
            >
              {/* エージェント選択ボタン */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                {(() => {
                  const on = selectedAgent === 'strategy_planning';
                  return (
                    <Box
                      component="button"
                      onClick={handleStrategyPlanning}
                      disabled={isLoading || isStreaming}
                      sx={{
                        fontFamily: 'inherit',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.9,
                        fontSize: 12,
                        fontWeight: 800,
                        px: 1.5,
                        py: 0.9,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: '0.15s',
                        border: `1.5px solid ${on ? tokens.teal : 'transparent'}`,
                        bgcolor: on
                          ? themeMode === 'dark'
                            ? 'rgba(45,212,191,0.12)'
                            : '#eafaf6'
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : '#f2f4f8',
                        color: on ? tokens.teal : tokens.muted,
                        '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                      }}
                    >
                      <Box
                        component="img"
                        src={IMG.investigate.strategy}
                        alt=""
                        sx={{ width: 18, height: 18, objectFit: 'contain' }}
                      />
                      戦略
                    </Box>
                  );
                })()}
                {(() => {
                  const on = selectedAgent === 'perplexity_search';
                  return (
                    <Box
                      component="button"
                      onClick={handleCrossSearch}
                      disabled={isLoading || isStreaming}
                      sx={{
                        fontFamily: 'inherit',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.9,
                        fontSize: 12,
                        fontWeight: 800,
                        px: 1.5,
                        py: 0.9,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: '0.15s',
                        border: `1.5px solid ${on ? tokens.teal : 'transparent'}`,
                        bgcolor: on
                          ? themeMode === 'dark'
                            ? 'rgba(45,212,191,0.12)'
                            : '#eafaf6'
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : '#f2f4f8',
                        color: on ? tokens.teal : tokens.muted,
                        '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                      }}
                    >
                      <Box
                        component="img"
                        src={IMG.investigate.webSearch}
                        alt=""
                        sx={{ width: 18, height: 18, objectFit: 'contain' }}
                      />
                      {searchPeriodLabel ? `横断検索・${searchPeriodLabel}` : '横断検索'}
                    </Box>
                  );
                })()}
              </Box>

              {/* 入力行 */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  placeholder={
                    selectedAgent === 'strategy_planning'
                      ? '戦略立案エージェントに質問...'
                      : selectedAgent === 'perplexity_search'
                        ? searchConfig
                          ? `横断検索エージェントに質問（期間: ${
                              searchConfig.period_type === 'day'
                                ? '1日'
                                : searchConfig.period_type === 'week'
                                  ? '1週間'
                                  : searchConfig.period_type === 'month'
                                    ? '1ヶ月'
                                    : '1年'
                            }）...`
                          : '横断検索エージェントに質問...'
                        : currentItem
                          ? 'このファクトについて調査したいことを入力...'
                          : '調査したいことを自由に入力...'
                  }
                  disabled={isLoading || isStreaming}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (currentInput.trim()) {
                        handleSendMessageUnified(currentInput);
                      }
                    }
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                      fontSize: 14,
                      lineHeight: 1.5,
                      bgcolor: themeMode === 'dark' ? 'rgba(0,0,0,0.2)' : '#fff',
                      color: tokens.ink,
                      '& fieldset': { borderColor: tokens.line, borderWidth: '1.5px' },
                      '&:hover fieldset': { borderColor: `${tokens.teal}66` },
                      '&.Mui-focused fieldset': { borderColor: tokens.teal },
                    },
                    '& .MuiInputBase-input::placeholder': { color: tokens.faint, opacity: 1 },
                  }}
                />

                {/* 送信ボタン */}
                <Button
                  onClick={() => {
                    if (currentInput.trim()) {
                      handleSendMessageUnified(currentInput);
                    }
                  }}
                  disabled={!currentInput.trim() || isLoading || isStreaming || isStreaming}
                  startIcon={isLoading || isStreaming ? null : <SendIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: 13.5,
                    color: '#fff',
                    borderRadius: '13px',
                    px: 2.25,
                    py: 1.5,
                    background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
                    boxShadow: '0 8px 20px rgba(40,50,90,.2)',
                    '&:hover': {
                      background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
                      filter: 'brightness(0.97)',
                      boxShadow: '0 8px 20px rgba(40,50,90,.2)',
                    },
                    '&.Mui-disabled': { background: '#cbd2e0', color: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  {isLoading || isStreaming ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    '送信'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 検索期間指定ダイアログ */}
      <SearchPeriodDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onConfirm={handleSearchWithPeriod}
      />
    </Box>
  );
};

// チャット履歴コンポーネント
const ChatHistory: React.FC<{
  messages: InvestigationMessage[];
  onEditMessage?: (messageId: string, newContent: string) => void;
  onExecuteStrategy?: (strategyContent: string) => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
  streamingAgent?: string;
  themeMode?: 'dark' | 'light';
  userPhotoURL?: string;
  userDisplayName?: string;
}> = ({
  messages,
  onEditMessage,
  onExecuteStrategy,
  isLoading = false,
  isStreaming = false,
  streamingContent = '',
  streamingAgent = '',
  themeMode = 'dark',
  userPhotoURL,
  userDisplayName,
}) => {
  const tokens = useThemeColors();
  // テーマに応じた色定義
  const assistantBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#fff';
  const assistantBorder =
    themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(20, 24, 40, 0.08)';
  const textColor = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textSecondary = themeMode === 'dark' ? '#e2e8f0' : '#374151';
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const codeBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';
  const codeBorder = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const codeText = themeMode === 'dark' ? '#cbd5e1' : '#374151';
  const tableBorder = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const tableHeaderBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
  const blockquoteText = themeMode === 'dark' ? '#94a3b8' : '#6b7280';
  // 編集中のメッセージIDと内容を管理
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');

  // 最新のメッセージかどうかを判定
  const isLatestMessage = (messageId: string) => {
    const assistantMessages = messages.filter((m) => m.role === 'assistant');
    if (assistantMessages.length === 0) return false;
    return assistantMessages[assistantMessages.length - 1].id === messageId;
  };

  // 編集開始
  const handleStartEdit = (message: InvestigationMessage) => {
    setEditingMessageId(message.id);
    setEditedContent(message.content);
  };

  // 編集確定
  const handleConfirmEdit = async (messageId: string) => {
    if (onEditMessage && editedContent.trim()) {
      await onEditMessage(messageId, editedContent);
      setEditingMessageId(null);
      setEditedContent('');
    }
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  // 戦略実行
  const handleExecute = (strategyContent: string) => {
    if (onExecuteStrategy) {
      onExecuteStrategy(strategyContent);
    }
  };

  if (messages.length === 0 && !isLoading && !isStreaming) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1.75,
          color: tokens.muted,
        }}
      >
        <Box
          component="img"
          src={IMG.owl.followupPanel}
          alt=""
          sx={{
            width: 120,
            height: 120,
            objectFit: 'contain',
            filter: 'drop-shadow(0 8px 16px rgba(30,30,70,.16))',
          }}
        />
        <Typography sx={{ fontSize: 16, fontWeight: 900, color: tokens.ink }}>
          調査を開始しましょう
        </Typography>
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 320 }}>
          上のボタンか下の入力欄から質問を送信してください
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {messages.map((message) => {
        const isUser = message.role === 'user';
        const agentMeta = message.agent ? AGENT_META[message.agent] : undefined;
        return (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              gap: 1.4,
              mb: 2.25,
              alignItems: 'flex-start',
              flexDirection: isUser ? 'row-reverse' : 'row',
            }}
          >
            {/* アバター */}
            {isUser ? (
              <Avatar
                src={userPhotoURL || undefined}
                alt=""
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  fontSize: 15,
                  fontWeight: 800,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #818cf8, #5b56e0)',
                  border: '2px solid rgba(255,255,255,.7)',
                }}
              >
                {(userDisplayName?.trim()?.charAt(0) || 'U').toUpperCase()}
              </Avatar>
            ) : (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #fdf6e3, #fff)',
                  border: '1px solid rgba(201,162,77,.35)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 3px 8px rgba(30,30,70,.07)',
                }}
              >
                <Box
                  component="img"
                  src={IMG.chat.avatar}
                  alt=""
                  sx={{ width: 34, height: 34, objectFit: 'contain' }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: editingMessageId === message.id ? '95%' : '76%',
                alignItems: isUser ? 'flex-end' : 'flex-start',
              }}
            >
              {/* エージェント名表示 */}
              {agentMeta ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontSize: 11,
                    fontWeight: 900,
                    color: tokens.teal,
                    mb: 0.6,
                    ml: '2px',
                  }}
                >
                  <Box
                    component="img"
                    src={agentMeta.icon}
                    alt=""
                    sx={{ width: 19, height: 19, objectFit: 'contain' }}
                  />
                  {agentMeta.label}
                </Box>
              ) : (
                message.agent && (
                  <Typography
                    sx={{
                      display: 'block',
                      mb: 0.6,
                      ml: '2px',
                      opacity: 0.8,
                      fontSize: 11,
                      fontWeight: 900,
                      color: tokens.teal,
                    }}
                  >
                    {message.agent}
                  </Typography>
                )
              )}
              <Paper
                elevation={0}
                sx={{
                  py: 1.5,
                  px: 2,
                  width: editingMessageId === message.id ? '100%' : 'auto',
                  background: isUser ? 'linear-gradient(125deg, #6366f1, #5b56e0)' : assistantBg,
                  color: isUser ? '#fff' : textColor,
                  border: '1px solid',
                  borderColor: isUser ? 'transparent' : assistantBorder,
                  borderRadius: '12px',
                  borderTopLeftRadius: isUser ? '16px' : '5px',
                  borderTopRightRadius: isUser ? '5px' : '16px',
                  boxShadow: isUser
                    ? '0 6px 16px rgba(91,86,224,.22)'
                    : '0 3px 12px rgba(30,30,70,.05)',
                }}
              >
                {/* メッセージ内容 - 編集モードまたは通常表示 */}
                {editingMessageId === message.id ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      width: '100%',
                      minWidth: '600px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '0.78rem',
                        width: '100%',
                      },
                      '& .MuiOutlinedInput-input': {
                        width: '100%',
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      // 基本的なテキストの折り返し設定
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        color: message.role === 'user' ? '#fff' : textColor,
                        fontWeight: 'bold',
                        marginTop: 2,
                        marginBottom: 1,
                        wordBreak: 'break-word',
                      },
                      '& p': {
                        margin: 0,
                        lineHeight: 1.55,
                        fontSize: '0.78rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        color: message.role === 'user' ? '#fff' : textSecondary,
                      },
                      '& ul, & ol': {
                        paddingLeft: 2,
                        marginBottom: 1,
                        fontSize: '0.78rem',
                        lineHeight: 1.55,
                        color: message.role === 'user' ? '#fff' : textSecondary,
                      },
                      '& li': {
                        marginBottom: 0.5,
                        fontSize: '0.78rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      },
                      '& a': {
                        color: message.role === 'user' ? '#fff' : accentColor,
                        textDecoration: 'underline',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                      },
                      '& code': {
                        backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.2)' : codeBg,
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontSize: '0.78rem',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        border:
                          message.role === 'user'
                            ? '1px solid rgba(255,255,255,0.3)'
                            : `1px solid ${codeBorder}`,
                        color: message.role === 'user' ? '#fff' : codeText,
                      },
                      '& pre': {
                        backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : codeBg,
                        color: message.role === 'user' ? '#fff' : textSecondary,
                        padding: 2,
                        borderRadius: 1,
                        overflowX: 'auto',
                        marginBottom: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        border:
                          message.role === 'user'
                            ? '1px solid rgba(255,255,255,0.2)'
                            : `1px solid ${codeBorder}`,
                      },
                      '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor:
                          message.role === 'user' ? 'rgba(255,255,255,0.5)' : 'primary.main',
                        paddingLeft: 2,
                        marginLeft: 0,
                        fontStyle: 'italic',
                        wordBreak: 'break-word',
                        color: message.role === 'user' ? 'rgba(255,255,255,0.9)' : blockquoteText,
                      },
                      '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: 1,
                        tableLayout: 'fixed',
                      },
                      '& th, & td': {
                        border: '1px solid',
                        borderColor:
                          message.role === 'user' ? 'rgba(255,255,255,0.3)' : tableBorder,
                        padding: 1,
                        textAlign: 'left',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        color: message.role === 'user' ? '#fff' : textSecondary,
                      },
                      '& th': {
                        backgroundColor:
                          message.role === 'user' ? 'rgba(255,255,255,0.1)' : tableHeaderBg,
                        fontWeight: 'bold',
                        color: message.role === 'user' ? '#fff' : textColor,
                      },
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </Box>
                )}

                {/* 戦略エージェントの編集・実行ボタン */}
                {message.agent === 'strategy_planning' &&
                  message.role === 'assistant' &&
                  isLatestMessage(message.id) && (
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 1 }}>
                      {editingMessageId === message.id ? (
                        <>
                          <Button
                            size="small"
                            startIcon={<CheckIcon />}
                            onClick={() => handleConfirmEdit(message.id)}
                            color="primary"
                            variant="contained"
                          >
                            確定
                          </Button>
                          <Button
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={handleCancelEdit}
                            color="inherit"
                          >
                            戻る
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleStartEdit(message)}
                            disabled={isLoading || isStreaming}
                          >
                            編集
                          </Button>
                          <Button
                            size="small"
                            startIcon={<PlayIcon />}
                            onClick={() =>
                              handleExecute(
                                editingMessageId === message.id ? editedContent : message.content,
                              )
                            }
                            color="primary"
                            variant="contained"
                            disabled={isLoading || isStreaming}
                          >
                            実行
                          </Button>
                        </>
                      )}
                    </CardActions>
                  )}
              </Paper>

              {/* タイムスタンプ（吹き出しの外側） */}
              <Typography
                sx={{
                  fontSize: 10,
                  color: tokens.faint,
                  mt: '5px',
                  mx: '2px',
                  alignSelf: isUser ? 'flex-end' : 'flex-start',
                }}
              >
                {new Date(message.timestamp).toLocaleTimeString('ja-JP', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* ストリーミング中のメッセージ表示、または開始前のローディング表示 */}
      {(isStreaming || isLoading) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.4,
            mb: 2.25,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              flexShrink: 0,
              background: 'linear-gradient(135deg, #fdf6e3, #fff)',
              border: '1px solid rgba(201,162,77,.35)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 3px 8px rgba(30,30,70,.07)',
            }}
          >
            <Box
              component="img"
              src={IMG.chat.avatar}
              alt=""
              sx={{ width: 34, height: 34, objectFit: 'contain' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '76%' }}>
            {streamingAgent &&
              (() => {
                const meta = AGENT_META[streamingAgent];
                return (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: 11,
                      fontWeight: 900,
                      color: tokens.teal,
                      mb: 0.6,
                      ml: '2px',
                    }}
                  >
                    {meta && (
                      <Box
                        component="img"
                        src={meta.icon}
                        alt=""
                        sx={{ width: 19, height: 19, objectFit: 'contain' }}
                      />
                    )}
                    {meta ? meta.label : streamingAgent}
                  </Box>
                );
              })()}
            <Paper
              elevation={0}
              sx={{
                py: 1.5,
                px: 2,
                background: assistantBg,
                color: textColor,
                border: '1px solid',
                borderColor: assistantBorder,
                borderRadius: '12px',
                borderTopLeftRadius: '5px',
                boxShadow: '0 3px 12px rgba(30,30,70,.05)',
              }}
            >
              {/* ストリーミングコンテンツがある場合はそれを表示、ない場合は「考え中」を表示 */}
              {streamingContent ? (
                <Box
                  sx={{
                    // Markdown表示スタイルを共通化
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    '& p': {
                      margin: 0,
                      lineHeight: 1.55,
                      fontSize: '0.78rem',
                      color: textSecondary,
                    },
                    '& ul, & ol': {
                      paddingLeft: 2,
                      marginBottom: 1,
                      fontSize: '0.78rem',
                      lineHeight: 1.55,
                      color: textSecondary,
                    },
                    '& li': { marginBottom: 0.5, fontSize: '0.78rem' },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: textSecondary, fontSize: '0.78rem' }}
                  >
                    {AGENT_META[streamingAgent]?.label
                      ? `${AGENT_META[streamingAgent].label}が考え中`
                      : '考え中'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      '@keyframes ipwave': {
                        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.45 },
                        '30%': { transform: 'translateY(-5px)', opacity: 1 },
                      },
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: accentColor,
                          animation: 'ipwave 1.1s infinite ease-in-out',
                          animationDelay: `${i * 0.15}s`,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// 検索期間指定ダイアログ
const SearchPeriodDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (config: SearchPeriodConfig) => void;
}> = ({ open, onClose, onConfirm }) => {
  const tokens = useThemeColors();
  const [recencyValue, setRecencyValue] = useState('none');

  const recencyOptions = [
    { value: 'none', label: '指定なし（全期間）' },
    { value: 'day', label: '過去1日' },
    { value: 'week', label: '過去1週間' },
    { value: 'month', label: '過去1ヶ月' },
    { value: 'year', label: '過去1年' },
  ];

  const handleConfirm = () => {
    const config: SearchPeriodConfig =
      recencyValue === 'none'
        ? { period_type: 'none' as any } // 指定なしの場合
        : { period_type: recencyValue as 'day' | 'week' | 'month' | 'year' };

    onConfirm(config);
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      icon={<Box component="img" src={IMG.investigate.period} alt="" />}
      title="検索期間を指定"
      subtitle="横断検索でさかのぼる対象期間を選んでください"
      size="md"
      actions={
        <>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 13,
              color: tokens.ink,
              border: `1.5px solid ${tokens.line}`,
              borderRadius: '11px',
              px: 2.25,
              py: 1,
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            startIcon={<SearchIcon sx={{ fontSize: 15 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 13,
              color: '#fff',
              borderRadius: '11px',
              px: 2.5,
              py: 1,
              background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
              boxShadow: '0 8px 20px rgba(40,50,90,.2)',
              '&:hover': {
                background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
                filter: 'brightness(0.97)',
                boxShadow: '0 8px 20px rgba(40,50,90,.2)',
              },
            }}
          >
            この期間で検索
          </Button>
        </>
      }
    >
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.6, mb: 2.25 }}>
        <Box
          component="img"
          src={IMG.investigate.webSearch}
          alt=""
          sx={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0 }}
        />
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: tokens.ink }}>
            検索期間を指定
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.4, lineHeight: 1.45 }}>
            横断検索でさかのぼる対象期間を選んでください。
          </Typography>
        </Box>
      </Box>

      {/* 期間オプション */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
        {recencyOptions.map((option) => {
          const on = recencyValue === option.value;
          return (
            <Box
              key={option.value}
              component="button"
              onClick={() => setRecencyValue(option.value)}
              sx={{
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.75,
                py: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: '0.12s',
                fontWeight: 800,
                fontSize: 13.5,
                textAlign: 'left',
                border: `1.5px solid ${on ? tokens.teal : tokens.line}`,
                bgcolor: on ? `${tokens.teal}1a` : tokens.paper,
                color: on ? tokens.teal : tokens.ink,
                '&:hover': { borderColor: `${tokens.teal}66` },
              }}
            >
              <TimeIcon sx={{ fontSize: 17, color: on ? tokens.teal : tokens.faint }} />
              <Box component="span" sx={{ flex: 1 }}>
                {option.label}
              </Box>
              <Box
                component="span"
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  flexShrink: 0,
                  border: `2px solid ${on ? tokens.teal : tokens.line}`,
                  bgcolor: on ? tokens.teal : 'transparent',
                  display: 'grid',
                  placeItems: 'center',
                  '&::after': on
                    ? {
                        content: '""',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: '#fff',
                      }
                    : {},
                }}
              />
            </Box>
          );
        })}
      </Box>
    </AppDialog>
  );
};

export default InvestigationPage;
